import {
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
  MailOutlined
} from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { useDispatch   } from "react-redux";
import { Form, Input, Button, Checkbox, Typography, Card, message } from 'antd';
import { NavLink, useNavigate } from 'react-router-dom';
import { loginUser } from '@/stores/slice/authSlice';
import { Controller, useForm } from 'react-hook-form';
import InspectionBtn from '@/components/InspectionBtn';
import Logo from "@/assets/inspections1.png"
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { useAuth } from '@/hooks/useAuth';
 import toast from 'react-hot-toast';
const { Title, Text, Link } = Typography;

const LoginForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();

  // Use the hook instead of direct selector
  const { role, isAuthenticated, isLoading } = useAuth();
  const [hasError, setError] = useState("");
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();

  const [loader, setLoader] = useState(false);

  const onSubmit = async (data) => {
  let browserFingerprint;
  try {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    browserFingerprint = result.visitorId;
  } catch (fpError) {
    console.error("Fingerprint generation failed:", fpError);
  }

  setLoader(true);
  setError(""); 

  try {
    const payload = {
      email: data?.email?.trim(),
      password: data?.password,
      fingerprint: browserFingerprint,
    };

    // unwrap() will throw error if rejected
    const res = await dispatch(loginUser(payload)).unwrap();

    // ✅ Login success
    toast.success("Logged in successfully");
    setError("");
      setLoader(false);

  } catch (err) {
    // ✅ Login failed
    console.error("Login error:", err);
    setError(err?.message || "Invalid credentials");
    toast.error(err || "Login failed");
    setLoader(false);
  }
};
  // Handle navigation after successful login
  useEffect(() => {
    if (isAuthenticated && role) {
      console.log("Navigating based on role:", role);

      switch (role) {
        case "ADMIN":
          navigate("/admin/dashboard", { replace: true });
          break;
        case "USER":
          navigate("/user/dashboard", { replace: true });
          break;
        case "OWNER":
          navigate("/manager/dashboard", { replace: true });
          break;
        default:
          navigate("/unauthorized", { replace: true });
      }
    }
  }, [role, isAuthenticated, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-[#0087af] to-[#00ae68] p-4">
      <Card className="w-full max-w-md shadow-2xl">
        {hasError && <p className='text-center text-red-500 mb-4'>{hasError}</p>}

        <div className="flex justify-center items-center mb-6">
          <img
            src={Logo}
            alt="Company Logo"
            className="h-20 w-20 rounded object-contain"
          />
          <span className="text-[28px] font-extrabold tracking-tight bg-gradient-to-r from-[#0087af] to-[#00ae68] bg-clip-text text-transparent">
            SpotFlash
          </span>
        </div>

        <Form
          layout="vertical"
          onFinish={handleSubmit(onSubmit)}
          initialValues={{ remember: true }}
        >
          {/* Email */}
          <Form.Item
            label="Email Address"
            name="email"
            validateStatus={errors.email ? 'error' : ''}
            help={errors.email?.message}
          >
            <Controller
              name="email"
              control={control}
              rules={{
                required: "Email Required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address"
                }
              }}
              render={({ field }) => (
                <Input
                  prefix={<MailOutlined className="me-2" />}
                  placeholder="Email"
                  {...field}
                  size="large"
                />
              )}
            />
          </Form.Item>

          {/* Password */}
          <Form.Item
            label="Password"
            name="password"
            validateStatus={errors.password ? 'error' : ''}
            help={errors.password?.message}
          >
            <Controller
              name="password"
              control={control}
              rules={{
                required: "Password Required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters"
                }
              }}
              render={({ field }) => (
                <Input.Password
                  {...field}
                  prefix={<LockOutlined className="me-2" />}
                  placeholder="Password"
                  size="large"
                />
              )}
            />
          </Form.Item>

          {/* Remember + Forgot */}
          <Form.Item>
            <div className="flex justify-between items-center">
              <NavLink
                to="/forgetpassword"
                className='text-green-500 hover:text-green-600'
              >
                Forgot your password?
              </NavLink>
            </div>
          </Form.Item>

          {/* Submit */}
          <Form.Item>
            <InspectionBtn
              className='w-100 rounded-2xl'
              type="primary"
              htmlType="submit"
              block
              loading={loader || isLoading}
              disabled={loader || isLoading}
            >
              {loader || isLoading ? 'Signing in...' : 'Sign in'}
            </InspectionBtn>
          </Form.Item>

          {/* Toggle to Signup */}
          <div className="text-center">
            <Text>
              Don't have an account?{' '}
              <Button
                type="link"
                onClick={() => navigate("/verify")}
                style={{ padding: 0, color: '#03AE8C' }}
              >
                Sign up
              </Button>
            </Text>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default LoginForm;